/**
 * useActivityUrlState — sibling hook for the UserDrilldown Activity tab's
 * URL-mirrored filter state (Plan 08-03 / ACT-04 / ACT-06).
 *
 * Mirrors `useFeedbackUrlState`'s read-from-URL → toApiExtras pattern, but
 * lives in its own keyset so it does NOT pollute either:
 *
 *   - `useUrlFilters.dirty`     (global FilterBar — different keys)
 *   - `useFeedbackUrlState.dirty` (Feedback page — different keys)
 *
 * Why a SEPARATE hook instead of extending `useFeedbackUrlState`:
 *
 *   The Activity tab and the Feedback page are mounted on different routes
 *   (UserDrilldownPage vs FeedbackPage), but during the brief navigation
 *   overlap window both hooks may read the same `useSearchParams`
 *   reference. Encoding Activity-tab params under feedback URL keys would
 *   make `useFeedbackUrlState.dirty` flicker as the Activity tab writes —
 *   that is the Bug F1 trap (RESEARCH Pattern 4 / WARNING-08). Sibling
 *   hooks with disjoint keysets sidestep the trap by construction.
 *
 * URL keys owned by this hook:
 *   - `activity_expanded` — comma-list of trace IDs whose detail panels
 *     are inline-expanded in the Activity tab. Capped at EXPANDED_MAX.
 *   - `rating`             — one of 8 enum values (8th = 'any', the default).
 *                            Encodes both the numeric range and the
 *                            hasFeedback selection in a single param —
 *                            'rated' / 'unrated' / 'with_text' route to
 *                            `hasFeedback`, the rest to `ratingMin/Max`.
 *   - `search`             — free-text prompt/response substring (≤200 chars).
 *   - `errorsOnly`         — presence-encoded boolean. `?errorsOnly=1`
 *                            ↔ true, absent ↔ false.
 *
 * Defaults are elided from the URL (mirror `stripDefaults()` discipline
 * from `filters-schema.ts`).
 */

import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

import { EXPANDED_MAX } from '@/lib/expanded-rows-cap';
import type { TraceListExtras, TraceListSort } from '@/services/api-types';

// ---------------------------------------------------------------------------
// Shape
// ---------------------------------------------------------------------------

/** URL keys owned by this hook. Disjoint from FEEDBACK_URL_KEYS. */
const ACTIVITY_URL_KEYS = [
  'activity_expanded',
  'rating',
  'search',
  'errorsOnly',
  'activity_sort',
] as const;

/**
 * Locked rating-select enum (CONTEXT D §"URL state — new params" + §"Rating
 * select option → API params mapping"). 8 values; `'any'` is the elided
 * default. Numeric-range values map to ratingMin/Max; the three string-codes
 * map to the backend's `hasFeedback` filter.
 */
export const ACTIVITY_RATING_VALUES = [
  'any',
  '5',
  '4+',
  '3+',
  '1-2',
  'rated',
  'unrated',
  'with_text',
] as const;
export type ActivityRating = (typeof ACTIVITY_RATING_VALUES)[number];

const DEFAULT_RATING: ActivityRating = 'any';

/** Per-id charset cap mirroring `useFeedbackUrlState.parseExpanded`. */
const TRACE_ID_RE = /^[A-Za-z0-9_-]+$/;

const SEARCH_MAX_LEN = 200;

/**
 * Allow-list of TraceListSort values the Activity-tab toolbar may set via the
 * `activity_sort` URL key. Mirrors the backend `SORTS` enum in
 * `dashboard/routes/api/traces.ts` — keep in sync. Default `'newest'` is
 * elided from the URL.
 */
const ACTIVITY_SORT_VALUES = [
  'newest',
  'oldest',
  'latency_desc',
  'latency_asc',
  'cost_desc',
  'cost_asc',
  'rating_desc',
  'rating_asc',
  'name_desc',
  'name_asc',
] as const satisfies readonly TraceListSort[];

const DEFAULT_SORT: TraceListSort = 'newest';

// ---------------------------------------------------------------------------
// Parse helpers
// ---------------------------------------------------------------------------

function parseRating(raw: string | null): ActivityRating {
  if (!raw) return DEFAULT_RATING;
  // Length-cap before string compare to avoid pathological-length CPU spend.
  if (raw.length > 16) return DEFAULT_RATING;
  if ((ACTIVITY_RATING_VALUES as readonly string[]).includes(raw)) {
    return raw as ActivityRating;
  }
  return DEFAULT_RATING;
}

function parseSearch(raw: string | null): string {
  if (!raw) return '';
  // Defence in depth: clamp on read so a deep-linked URL with a 5000-char
  // search blob is truncated before it ever hits TanStack Query's cache key.
  return raw.slice(0, SEARCH_MAX_LEN);
}

function parseSort(raw: string | null): TraceListSort {
  if (!raw) return DEFAULT_SORT;
  if (raw.length > 24) return DEFAULT_SORT;
  if ((ACTIVITY_SORT_VALUES as readonly string[]).includes(raw)) {
    return raw as TraceListSort;
  }
  return DEFAULT_SORT;
}

function parseErrorsOnly(raw: string | null): boolean {
  // Presence-encoded — `?errorsOnly=1` ↔ true. Anything else (including
  // 'true', 'false', '0', '') is treated as absence-of-truthy → false. We
  // only generate '1' on the write side, so the read side only needs to
  // honour that one symbol.
  return raw === '1';
}

function parseExpanded(raw: string | null): Set<string> {
  if (!raw) return new Set();
  const seen = new Set<string>();
  for (const candidate of raw.split(',')) {
    const id = candidate.trim();
    if (!id) continue;
    if (id.length > 64) continue;
    if (!TRACE_ID_RE.test(id)) continue;
    seen.add(id);
    if (seen.size >= EXPANDED_MAX) break;
  }
  return seen;
}

// ---------------------------------------------------------------------------
// Write helpers — mutate `params` in place; never touch foreign keys.
// ---------------------------------------------------------------------------

function writeRating(params: URLSearchParams, next: ActivityRating): void {
  if (next === DEFAULT_RATING) {
    params.delete('rating');
  } else {
    params.set('rating', next);
  }
}

function writeSearch(params: URLSearchParams, next: string): void {
  const clamped = next.slice(0, SEARCH_MAX_LEN);
  if (!clamped) {
    params.delete('search');
  } else {
    params.set('search', clamped);
  }
}

function writeSort(params: URLSearchParams, next: TraceListSort): void {
  if (next === DEFAULT_SORT) {
    params.delete('activity_sort');
  } else {
    params.set('activity_sort', next);
  }
}

function writeErrorsOnly(params: URLSearchParams, next: boolean): void {
  if (next) {
    params.set('errorsOnly', '1');
  } else {
    params.delete('errorsOnly');
  }
}

function writeExpanded(params: URLSearchParams, next: Set<string>): void {
  if (next.size === 0) {
    params.delete('activity_expanded');
    return;
  }
  // Cap and dedupe at the WRITE side too so callers can't sneak past the
  // read clamp.
  const clean: string[] = [];
  for (const id of next) {
    if (typeof id !== 'string') continue;
    if (id.length > 64) continue;
    if (!TRACE_ID_RE.test(id)) continue;
    clean.push(id);
    if (clean.length >= EXPANDED_MAX) break;
  }
  if (clean.length === 0) {
    params.delete('activity_expanded');
  } else {
    params.set('activity_expanded', clean.join(','));
  }
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export interface UseActivityUrlStateResult {
  /** Currently committed rating (one of 8 enum values; default 'any'). */
  rating: ActivityRating;
  setRating: (next: ActivityRating) => void;

  /** Currently committed search substring (≤200 chars; default ''). */
  search: string;
  setSearch: (next: string) => void;

  /** Currently committed errorsOnly flag (default false). */
  errorsOnly: boolean;
  setErrorsOnly: (next: boolean) => void;

  /**
   * Currently committed server-side sort for the Activity feed (default
   * 'newest'). Backed by the `activity_sort` URL key; default is elided so a
   * pristine URL stays clean.
   */
  sort: TraceListSort;
  setSort: (next: TraceListSort) => void;

  /** Read-only Set of currently-expanded trace IDs. */
  expandedSet: ReadonlySet<string>;
  toggleExpanded: (traceId: string) => void;

  /**
   * Map the URL-state into the `TraceListExtras` shape that
   * `useTracesList(filters, extras)` expects. Defaults are omitted so the
   * extras stay minimal — TanStack Query's cache key only carries the keys
   * that actually moved off default.
   */
  toApiExtras: () => TraceListExtras;
}

/** Map a rating select value to API params (CONTEXT §"Rating select option →
 *  API params mapping"). Pure — extracted for unit testing. */
function ratingToApi(
  rating: ActivityRating,
): Pick<TraceListExtras, 'ratingMin' | 'ratingMax' | 'hasFeedback'> {
  switch (rating) {
    case 'any':
      return {};
    case '5':
      return { ratingMin: 5, ratingMax: 5 };
    case '4+':
      return { ratingMin: 4, ratingMax: 5 };
    case '3+':
      return { ratingMin: 3, ratingMax: 5 };
    case '1-2':
      return { ratingMin: 1, ratingMax: 2 };
    case 'rated':
      return { hasFeedback: 'rated' };
    case 'unrated':
      return { hasFeedback: 'unrated' };
    case 'with_text':
      return { hasFeedback: 'with_text' };
  }
}

export function useActivityUrlState(): UseActivityUrlStateResult {
  const [searchParams, setSearchParams] = useSearchParams();

  // Committed state — derived directly from the URL on every render. We
  // intentionally do NOT keep a pending buffer (the Activity-tab toolbar is
  // live-apply, mirroring `useUrlFilters.setPending`). The dirty-flag bug
  // (Bug F1) only bites hooks that DO keep a pending buffer; sibling
  // live-apply hooks compose cleanly.
  const rating = useMemo(
    () => parseRating(searchParams.get('rating')),
    [searchParams],
  );
  const search = useMemo(
    () => parseSearch(searchParams.get('search')),
    [searchParams],
  );
  const errorsOnly = useMemo(
    () => parseErrorsOnly(searchParams.get('errorsOnly')),
    [searchParams],
  );
  const sort = useMemo(
    () => parseSort(searchParams.get('activity_sort')),
    [searchParams],
  );
  const expandedSet = useMemo(
    () => parseExpanded(searchParams.get('activity_expanded')),
    [searchParams],
  );

  const setRating = useCallback(
    (next: ActivityRating) => {
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);
          writeRating(params, next);
          return params;
        },
        { replace: false },
      );
    },
    [setSearchParams],
  );

  const setSearch = useCallback(
    (next: string) => {
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);
          writeSearch(params, next);
          return params;
        },
        { replace: false },
      );
    },
    [setSearchParams],
  );

  const setErrorsOnly = useCallback(
    (next: boolean) => {
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);
          writeErrorsOnly(params, next);
          return params;
        },
        { replace: false },
      );
    },
    [setSearchParams],
  );

  const setSort = useCallback(
    (next: TraceListSort) => {
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);
          writeSort(params, next);
          return params;
        },
        { replace: false },
      );
    },
    [setSearchParams],
  );

  const toggleExpanded = useCallback(
    (traceId: string) => {
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);
          // Re-parse from `prev` rather than closing over `expandedSet` so
          // rapid double-clicks (two toggles in one frame) layer onto the
          // freshest URL snapshot rather than racing on a stale closure.
          const current = parseExpanded(params.get('activity_expanded'));
          const next = new Set(current);
          if (next.has(traceId)) {
            next.delete(traceId);
          } else {
            // Cap-aware: silently drop the 11th distinct id rather than
            // evicting an oldest entry. Matches the read-side clamp and
            // keeps the URL bounded.
            if (next.size >= EXPANDED_MAX) {
              return params;
            }
            next.add(traceId);
          }
          writeExpanded(params, next);
          return params;
        },
        // Like the Feedback page's setExpanded, use replace:true so a
        // triage session that toggles 8 rows in 30s doesn't leave an
        // 8-deep back stack.
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const toApiExtras = useCallback((): TraceListExtras => {
    const extras: TraceListExtras = { ...ratingToApi(rating) };
    if (errorsOnly) extras.errorsOnly = true;
    if (search) extras.search = search;
    // Default sort elided — keeps TanStack Query's cache key minimal and the
    // existing toApiExtras tests (which assert `{}` for a pristine URL) green.
    if (sort !== DEFAULT_SORT) extras.sort = sort;
    return extras;
  }, [rating, search, errorsOnly, sort]);

  return {
    rating,
    setRating,
    search,
    setSearch,
    errorsOnly,
    setErrorsOnly,
    sort,
    setSort,
    expandedSet,
    toggleExpanded,
    toApiExtras,
  };
}

// Internal export for tests / future audits — keep ACTIVITY_URL_KEYS
// addressable from outside the module without re-exporting through the
// hook's surface.
export { ACTIVITY_URL_KEYS };
